import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  ACT_SECTION_FIELDS,
  SAT_SECTION_FIELDS,
  ScoreCategoryDef,
  SUBSCORE_SECTION_ORDER,
  subscoresForTest,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.constants';
import {
  selectBoosterDraft,
  setSubscore,
  updateActScores,
  updateSatScores,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import {
  ActScores,
  SatScores,
  TargetTest,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { ScoreField } from '../components/ScoreField';
import { SectionCard, SectionGroup } from '../components/SectionCard';
import { actCompositeMismatch, satTotalMatchesSections } from '../utils/stepNavigation';

const STRINGS = appStrings.modules.satActBooster;

const INTRO: Record<TargetTest, { title: string; body: string }> = {
  act: { title: STRINGS.scoreInput.actTitle, body: STRINGS.scoreInput.actBody },
  sat: { title: STRINGS.scoreInput.satTitle, body: STRINGS.scoreInput.satBody },
};

type ScoreInputStepProps = Readonly<{
  test: TargetTest;
}>;

export const ScoreInputStep = ({ test }: ScoreInputStepProps) => {
  const dispatch = useAppDispatch();
  const draft = useAppSelector(selectBoosterDraft);

  const sectionFields = test === 'act' ? ACT_SECTION_FIELDS : SAT_SECTION_FIELDS;
  const scores: ActScores | SatScores = test === 'act' ? draft.actScores : draft.satScores;
  const scoreValue = (key: string) => (scores as Record<string, number | null>)[key] ?? null;

  // SAT total is the sum of the two section scores; flag them when they disagree.
  const satTotalMismatch = test === 'sat' && !satTotalMatchesSections(draft.satScores);

  // ACT composite is usually the mean of the sections; a soft warning (never a gate).
  const compositeMismatch = test === 'act' && actCompositeMismatch(draft.actScores);

  const categories = subscoresForTest(test);
  const sections = SUBSCORE_SECTION_ORDER[test];

  const subscoreFor = (code: string) => draft.subscores.find((s) => s.categoryCode === code);

  const setSectionScore = (key: string, value: number | null) =>
    dispatch(
      test === 'act' ? updateActScores({ [key]: value }) : updateSatScores({ [key]: value }),
    );

  const writeActPercent = (code: string, value: number | null) =>
    dispatch(
      setSubscore({ categoryCode: code, percentCorrect: value, valueLow: null, valueHigh: null }),
    );

  const writeSatRange = (code: string, low: number | null, high: number | null) =>
    dispatch(
      setSubscore({ categoryCode: code, percentCorrect: null, valueLow: low, valueHigh: high }),
    );

  const renderSubscoreField = (category: ScoreCategoryDef) => {
    if (test === 'act') {
      return (
        <ScoreField
          key={category.code}
          id={`booster-subscore-${category.code}`}
          label={category.name}
          leadingLabel={category.shortName ?? category.name}
          fillLabel
          value={subscoreFor(category.code)?.percentCorrect ?? null}
          min={0}
          max={100}
          suffix={STRINGS.scoreField.percentUnit}
          onChange={(value) => writeActPercent(category.code, value)}
        />
      );
    }

    const current = subscoreFor(category.code);
    const low = current?.valueLow ?? null;
    const high = current?.valueHigh ?? null;
    const isInverted = low !== null && high !== null && low > high;

    return (
      <Box key={category.code} sx={fieldSx}>
        <Typography variant="text-sm-semibold" sx={labelSx}>
          {category.name}
        </Typography>
        <Box sx={rangeRowSx}>
          <ScoreField
            id={`booster-subscore-${category.code}-low`}
            label={STRINGS.scoreInput.rangeLow}
            leadingLabel={STRINGS.scoreInput.rangeLow}
            value={low}
            min={200}
            max={800}
            onChange={(value) => writeSatRange(category.code, value, high)}
          />
          <ScoreField
            id={`booster-subscore-${category.code}-high`}
            label={STRINGS.scoreInput.rangeHigh}
            leadingLabel={STRINGS.scoreInput.rangeHigh}
            value={high}
            min={200}
            max={800}
            errorText={isInverted ? STRINGS.scoreInput.rangeInverted : undefined}
            onChange={(value) => writeSatRange(category.code, low, value)}
          />
        </Box>
      </Box>
    );
  };

  return (
    <SectionCard>
      <Box>
        <Typography variant="text-lg-semibold" sx={titleSx}>
          {INTRO[test].title}
        </Typography>
        <Typography variant="text-sm-regular" sx={bodySx}>
          {INTRO[test].body}
        </Typography>
      </Box>

      <SectionGroup>
        <Typography variant="text-md-semibold" sx={sectionScoresLabelSx}>
          {STRINGS.scoreInput.sectionScores}
          <Box component="span" sx={requiredAsteriskSx}>
            {' *'}
          </Box>
        </Typography>
        <Box sx={gridSx}>
          {sectionFields.map((field) => (
            <ScoreField
              key={field.key}
              id={`booster-score-${test}-${field.key}`}
              label={STRINGS.scoreInput.sectionLabels[field.key]}
              leadingLabel={STRINGS.scoreInput.sectionLabels[field.key]}
              value={scoreValue(field.key)}
              min={field.min}
              max={field.max}
              optional={field.optional}
              rangeHint={`${field.min}–${field.max}`}
              errorText={
                satTotalMismatch && field.key === 'total'
                  ? STRINGS.scoreInput.totalMismatch
                  : undefined
              }
              warningText={
                compositeMismatch && field.key === 'composite'
                  ? STRINGS.scoreInput.compositeMismatch
                  : undefined
              }
              onChange={(value) => setSectionScore(field.key, value)}
            />
          ))}
        </Box>
      </SectionGroup>

      <Box sx={subscoresSx}>
        <Box>
          <Typography variant="text-lg-semibold" sx={titleSx}>
            {STRINGS.scoreInput.subscoresHeading[test]}
          </Typography>
          <Typography variant="text-sm-regular" sx={bodySx}>
            {STRINGS.scoreInput.subscoresBody[test]}
          </Typography>
        </Box>
        <Box sx={subscoreSectionsSx}>
          {sections.map((section) => (
            <Box key={section} sx={subscoreGroupSx}>
              <Typography variant="text-md-semibold" sx={subsectionHeadingSx}>
                {section}
              </Typography>
              <Box sx={subscoreColumnSx}>
                {categories
                  .filter((category) => category.section === section)
                  .map((category) =>
                    category.parentCode ? (
                      <Box key={category.code} sx={nestedFieldSx}>
                        {renderSubscoreField(category)}
                      </Box>
                    ) : (
                      renderSubscoreField(category)
                    ),
                  )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </SectionCard>
  );
};

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  marginTop: backrs.spacing['spacing-xs'],
  maxWidth: '600px',
});

const sectionScoresLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const requiredAsteriskSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/500'],
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  // 3 × 190 + 2 × 16 = 602 ≤ 620; a wider floor drops the row to two columns.
  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  columnGap: backrs.spacing['spacing-xl'],
  rowGap: backrs.spacing['spacing-xl'],
  maxWidth: '620px', // Design spec: section-scores grid capped at 620px.
});

const subscoreGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const subsectionHeadingSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const subscoreColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const nestedFieldSx: SxProps<Theme> = ({ backrs }) => ({
  paddingLeft: backrs.spacing['spacing-3xl'],
});

const fieldSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const rangeRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-sm'],
  '& > *': { flex: 1 },
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const subscoresSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  paddingTop: backrs.spacing['spacing-xl'],
});

const subscoreSectionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  // Wide enough that a long category label ("Preparing for Higher Math") keeps its value segment.
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  alignItems: 'start',
  columnGap: backrs.spacing['spacing-xl'],
  rowGap: backrs.spacing['spacing-2xl'],
  paddingTop: backrs.spacing['spacing-2xl'],
});
